// Registro central dos conjuntos de dados da Plataforma de Dados.
//
// DATASET_NAV  -> alimenta a navegação lateral presente em todas as telas.
// DATASET_META -> conteúdo descritivo de cada tela (descrição, organização,
//                 cobertura temporal, periodicidade, fontes e dicionário).
//
// Para publicar o conteúdo de uma tela basta adicionar a chave correspondente
// em DATASET_META. Enquanto a chave não existir, a tela continua funcionando
// normalmente e apenas os blocos descritivos deixam de ser exibidos.

export const DATASET_NAV = [
    {
        group: 'Municípios',
        selector: 'municipio',
        items: [
            { key: 'municipal-revenue', label: 'Receita Anual', path: '/data-platform/municipal-revenue' },
            { key: 'municipal-transfer-revenue', label: 'Receita de Transferências', path: '/data-platform/municipal-transfer-revenue' },
            { key: 'municipal-net-current-revenue', label: 'Receita Corrente Líquida', path: '/data-platform/municipal-net-current-revenue' },
            { key: 'municipal-annual-expense', label: 'Despesa Anual', path: '/data-platform/municipal-annual-expense' },
            { key: 'municipal-function-expense', label: 'Despesa por Função', path: '/data-platform/municipal-function-expense' },
        ],
    },
    {
        group: 'Estados',
        selector: 'estado',
        items: [
            { key: 'state-revenue', label: 'Receita Anual', path: '/data-platform/state-revenue' },
            { key: 'state-transfer-revenue', label: 'Receita de Transferências', path: '/data-platform/state-transfer-revenue' },
            { key: 'state-net-current-revenue', label: 'Receita Corrente Líquida', path: '/data-platform/state-net-current-revenue' },
            { key: 'state-annual-expense', label: 'Despesa Anual', path: '/data-platform/state-annual-expense' },
            { key: 'state-function-expense', label: 'Despesa por Função', path: '/data-platform/state-function-expense' },
        ],
    },
    {
        group: 'União',
        selector: 'uniao',
        items: [
            { key: 'dirbi', label: 'DIRBI', path: '/data-platform/dirbi' },
        ],
    },
];

// ---------------------------------------------------------------------------
// Trechos que se repetem, palavra por palavra, em várias bases. Ficam aqui para
// que uma eventual revisão de texto seja feita em um único lugar. Se o texto de
// alguma base passar a divergir, basta escrever o parágrafo direto na entrada.
// ---------------------------------------------------------------------------
const P_SICONFI = 'O Siconfi foi desenvolvido para centralizar, padronizar e ampliar a qualidade das informações contábeis e fiscais dos entes da Federação, permitindo maior consistência na produção, consolidação e análise das estatísticas fiscais do setor público.';

const P_SERIE_HISTORICA = 'A série histórica disponibilizada pela FiscalData combina os dados do <strong>Finbra, para os anos de 2002 a 2012</strong>, com os dados do <strong>Siconfi, a partir de 2013 até o período mais recente disponível</strong>.';

const P_TESOURO_REPASSES = 'Cabe ao Tesouro Nacional efetuar e registrar os repasses desses recursos aos entes federados, observando os critérios de distribuição e os prazos legalmente estabelecidos.';

const P_RCL_INDICADOR_BASE = 'A Receita Corrente Líquida é um dos principais indicadores fiscais utilizados na análise das finanças públicas. Sua apuração considera as receitas correntes arrecadadas pelo ente, realizadas as deduções previstas na legislação, e serve como referência para diversos limites e indicadores estabelecidos pela Lei de Responsabilidade Fiscal, como aqueles relacionados';

const FONTES_SICONFI_FINBRA = [
    { label: 'Finbra', periodo: '2002–2012' },
    { label: 'Siconfi', periodo: '2013–presente' },
];

const FONTE_TRANSFERENCIAS = [
    { label: 'Sistema de Transferências Constitucionais e Legais — Secretaria do Tesouro Nacional' },
];

const FONTE_RREO = [
    {
        label: 'Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi)',
        detalhe: 'Relatório Resumido da Execução Orçamentária (RREO) — Anexo 3: Demonstrativo da Receita Corrente Líquida',
    },
];

const ATUALIZACAO_SICONFI_ANUAL = 'A base é atualizada conforme a disponibilização de novos dados anuais pelo Siconfi.';
const ATUALIZACAO_TESOURO = 'A base é atualizada conforme a disponibilização de novos dados pelo Tesouro Nacional.';
const ATUALIZACAO_RREO = 'A base é atualizada conforme a disponibilização de novos demonstrativos do RREO no Siconfi.';

const ORGANIZACAO_STN = 'Secretaria do Tesouro Nacional';

// ---------------------------------------------------------------------------
// DICIONÁRIO DE DADOS
// O bloco já está implementado em DataPlataformListPage e aparece
// automaticamente assim que a chave `dicionario` for preenchida em uma base.
// Basta colocar os arquivos em `public/files/` e descomentar o trecho abaixo
// dentro da entrada desejada:
//
// dicionario: {
//     descricao: 'Baixe o dicionário para consultar a descrição de cada coluna da base.',
//     arquivos: [
//         { label: 'Dicionário de dados', formato: 'XLSX', url: '/files/dicionario-<base>.xlsx' },
//         { label: 'Dicionário de dados', formato: 'DOCX', url: '/files/dicionario-<base>.docx' },
//     ],
// },
// ---------------------------------------------------------------------------

export const DATASET_META = {
    // ----------------------------- MUNICÍPIOS -----------------------------
    'municipal-revenue': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de receitas orçamentárias dos Municípios brasileiros, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>receitas orçamentárias dos Municípios brasileiros</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das contas contábeis da série histórica</strong>, compatibilizando as diferentes estruturas e classificações utilizadas nas bases originais. Dessa forma, contas equivalentes são associadas a uma classificação comum, permitindo acompanhar de maneira consistente a evolução das receitas municipais mesmo diante de alterações nos planos de contas e nas estruturas de divulgação ao longo dos anos.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    'municipal-transfer-revenue': {
        tituloComplemento: 'Tesouro Nacional',
        // Texto curto usado no card da home /data-platform
        resumo: 'Transferências de recursos da União aos Municípios brasileiros, provenientes do sistema de Transferências Constitucionais e Legais do Tesouro Nacional.',
        descricao: [
            'A base reúne dados sobre as <strong>transferências de recursos da União aos Municípios brasileiros</strong>, provenientes do sistema de Transferências Constitucionais e Legais da Secretaria do Tesouro Nacional (STN).',
            'As transferências intergovernamentais constituem uma importante fonte de financiamento dos Municípios e desempenham papel relevante na distribuição de recursos entre os entes da Federação. Parte das receitas arrecadadas pela União é repartida com Estados, Distrito Federal e Municípios, conforme critérios e regras estabelecidos pela Constituição Federal e pela legislação específica de cada transferência.',
            P_TESOURO_REPASSES,
            'Entre as principais transferências destinadas aos Municípios estão o <strong>Fundo de Participação dos Municípios (FPM)</strong>, o <strong>Imposto sobre a Propriedade Territorial Rural (ITR)</strong>, as transferências relacionadas ao <strong>Fundeb</strong>, a compensação pela exportação de produtos industrializados, entre outros repasses previstos na legislação.',
            'Para facilitar a utilização e a comparação dos dados, a FiscalData realiza um processo de <strong>organização, harmonização e padronização das informações ao longo da série histórica</strong>, compatibilizando nomenclaturas e classificações das diferentes modalidades de transferência. Dessa forma, os dados podem ser analisados de maneira consistente por Município, período e tipo de transferência.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2026 },
        periodicidade: 'Mensal',
        fontesOriginais: FONTE_TRANSFERENCIAS,
        atualizacao: ATUALIZACAO_TESOURO,
    },

    'municipal-net-current-revenue': {
        tituloComplemento: 'Siconfi/RREO',
        // Texto curto usado no card da home /data-platform
        resumo: 'Receita Corrente Líquida (RCL) dos Municípios brasileiros, extraída do Anexo 3 do RREO, disponibilizado no Siconfi.',
        descricao: [
            'A base reúne dados da <strong>Receita Corrente Líquida (RCL) dos Municípios brasileiros</strong>, extraídos do <strong>Anexo 3 — Demonstrativo da Receita Corrente Líquida</strong>, integrante do Relatório Resumido da Execução Orçamentária (RREO), disponibilizado no Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi).',
            `${P_RCL_INDICADOR_BASE} às despesas com pessoal, à dívida pública e às operações de crédito.`,
            'O Anexo 3 do RREO apresenta a composição e a apuração da RCL dos Municípios, permitindo acompanhar sua evolução ao longo do tempo e analisar a capacidade de geração de receitas correntes de cada ente.',
            'A FiscalData organiza e padroniza as informações disponibilizadas pelo Siconfi, permitindo a comparação dos dados entre Municípios e períodos e facilitando a construção de séries históricas da Receita Corrente Líquida.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2015, fim: 2026 },
        periodicidade: 'Bimestral',
        fontesOriginais: FONTE_RREO,
        atualizacao: ATUALIZACAO_RREO,
    },

    'municipal-annual-expense': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de despesas orçamentárias dos Municípios brasileiros, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>despesas orçamentárias dos Municípios brasileiros</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das contas contábeis da série histórica</strong>, compatibilizando as diferentes estruturas, classificações e níveis de detalhamento utilizados nas bases originais. Dessa forma, contas equivalentes são associadas a uma classificação comum, permitindo acompanhar de maneira consistente a evolução das despesas municipais mesmo diante de alterações nos planos de contas e nas estruturas de divulgação ao longo dos anos.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    'municipal-function-expense': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de despesas orçamentárias dos Municípios brasileiros classificadas por função, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>despesas orçamentárias dos Municípios brasileiros classificadas por função</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            'A classificação por função permite identificar as principais áreas de atuação governamental às quais as despesas são destinadas, como <strong>Saúde, Educação, Administração, Assistência Social, Urbanismo, Saneamento, Segurança Pública</strong>, entre outras.',
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das classificações funcionais da despesa ao longo da série histórica</strong>, compatibilizando diferenças nas estruturas, nomenclaturas e formas de divulgação das bases originais. Dessa forma, classificações equivalentes são associadas a uma estrutura comum, permitindo acompanhar de maneira consistente a evolução dos gastos municipais por área de atuação ao longo do tempo.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    // ------------------------------- ESTADOS -------------------------------
    'state-revenue': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de receitas orçamentárias dos Estados brasileiros, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>receitas orçamentárias dos Estados brasileiros</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das contas contábeis da série histórica</strong>, compatibilizando as diferentes estruturas, classificações e níveis de detalhamento utilizados nas bases originais. Dessa forma, contas equivalentes são associadas a uma classificação comum, permitindo acompanhar de maneira consistente a evolução das receitas estaduais mesmo diante de alterações nos planos de contas e nas estruturas de divulgação ao longo dos anos.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    'state-transfer-revenue': {
        tituloComplemento: 'Tesouro Nacional',
        // Texto curto usado no card da home /data-platform
        resumo: 'Transferências de recursos da União aos Estados brasileiros e ao Distrito Federal, provenientes do sistema de Transferências Constitucionais e Legais do Tesouro Nacional.',
        descricao: [
            'A base reúne dados sobre as <strong>transferências de recursos da União aos Estados brasileiros e ao Distrito Federal</strong>, provenientes do sistema de Transferências Constitucionais e Legais da Secretaria do Tesouro Nacional (STN).',
            'As transferências intergovernamentais constituem uma importante fonte de financiamento dos governos estaduais e desempenham papel relevante na distribuição de recursos entre os entes da Federação. Parte das receitas arrecadadas pela União é repartida com os Estados e o Distrito Federal conforme critérios e regras estabelecidos pela Constituição Federal e pela legislação específica de cada transferência.',
            P_TESOURO_REPASSES,
            'Entre as principais transferências destinadas aos Estados e ao Distrito Federal estão o <strong>Fundo de Participação dos Estados e do Distrito Federal (FPE)</strong>, a compensação pela exportação de produtos industrializados, as transferências relacionadas ao <strong>Fundeb</strong>, entre outros repasses previstos na legislação.',
            'Para facilitar a utilização e a comparação dos dados, a FiscalData realiza um processo de <strong>organização, harmonização e padronização das informações ao longo da série histórica</strong>, compatibilizando nomenclaturas e classificações das diferentes modalidades de transferência. Dessa forma, os dados podem ser analisados de maneira consistente por Estado, período e tipo de transferência.',
        ],
        organizacao: ORGANIZACAO_STN,
        // Ano final confirmado pela equipe de dados (2026). O ano inicial, que
        // veio como "[ano inicial]" no texto, é o primeiro exercício retornado
        // por /estado-receita-transferencia-exercicios e coincide com a base
        // equivalente dos Municípios.
        coberturaTemporal: { inicio: 2002, fim: 2026 },
        periodicidade: 'Mensal',
        fontesOriginais: FONTE_TRANSFERENCIAS,
        atualizacao: ATUALIZACAO_TESOURO,
    },

    'state-net-current-revenue': {
        tituloComplemento: 'Siconfi/RREO',
        // Texto curto usado no card da home /data-platform
        resumo: 'Receita Corrente Líquida (RCL) dos Estados brasileiros, extraída do Anexo 3 do RREO, disponibilizado no Siconfi.',
        descricao: [
            'A base reúne dados da <strong>Receita Corrente Líquida (RCL) dos Estados brasileiros</strong>, extraídos do <strong>Anexo 3 — Demonstrativo da Receita Corrente Líquida</strong>, integrante do Relatório Resumido da Execução Orçamentária (RREO), disponibilizado no Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi).',
            `${P_RCL_INDICADOR_BASE} às despesas com pessoal, à dívida pública, às operações de crédito e à concessão de garantias.`,
            'O Anexo 3 do RREO apresenta a composição e a apuração da RCL dos Estados, permitindo acompanhar sua evolução ao longo do tempo e analisar a capacidade de geração de receitas correntes de cada ente.',
            'A FiscalData organiza e padroniza as informações disponibilizadas pelo Siconfi, permitindo a comparação dos dados entre Estados e períodos e facilitando a construção de séries históricas da Receita Corrente Líquida.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2015, fim: 2026 },
        periodicidade: 'Bimestral',
        fontesOriginais: FONTE_RREO,
        atualizacao: ATUALIZACAO_RREO,
    },

    'state-annual-expense': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de despesas orçamentárias dos Estados brasileiros, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>despesas orçamentárias dos Estados brasileiros</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das contas contábeis da série histórica</strong>, compatibilizando as diferentes estruturas, classificações e níveis de detalhamento utilizados nas bases originais. Dessa forma, contas equivalentes são associadas a uma classificação comum, permitindo acompanhar de maneira consistente a evolução das despesas estaduais mesmo diante de alterações nos planos de contas e nas estruturas de divulgação ao longo dos anos.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    'state-function-expense': {
        tituloComplemento: 'Siconfi/Finbra',
        // Texto curto usado no card da home /data-platform
        resumo: 'Dados anuais de despesas orçamentárias dos Estados brasileiros classificadas por função, provenientes do Siconfi e do Finbra.',
        descricao: [
            'A base reúne dados anuais de <strong>despesas orçamentárias dos Estados brasileiros classificadas por função</strong>, provenientes do Sistema de Informações Contábeis e Fiscais do Setor Público Brasileiro (Siconfi) e do Finanças do Brasil (Finbra).',
            P_SICONFI,
            'A classificação por função permite identificar as principais áreas de atuação governamental às quais as despesas são destinadas, como <strong>Saúde, Educação, Segurança Pública, Administração, Assistência Social, Previdência Social, Transporte e Saneamento</strong>, entre outras.',
            P_SERIE_HISTORICA,
            'Para garantir a comparabilidade dos dados ao longo do tempo, a FiscalData realiza um processo de <strong>harmonização e padronização das classificações funcionais da despesa ao longo da série histórica</strong>, compatibilizando diferenças nas estruturas, nomenclaturas e formas de divulgação das bases originais. Dessa forma, classificações equivalentes são associadas a uma estrutura comum, permitindo acompanhar de maneira consistente a evolução dos gastos estaduais por área de atuação ao longo do tempo.',
        ],
        organizacao: ORGANIZACAO_STN,
        coberturaTemporal: { inicio: 2002, fim: 2025 },
        periodicidade: 'Anual',
        fontesOriginais: FONTES_SICONFI_FINBRA,
        atualizacao: ATUALIZACAO_SICONFI_ANUAL,
    },

    // -------------------------------- UNIÃO --------------------------------
    dirbi: {
        tituloComplemento: 'Receita Federal',
        resumo: 'Dados da Declaração de Incentivos, Benefícios e Renúncias Fiscais (DIRBI), com valores de benefícios fiscais por beneficiário, município, tributo e programa de isenção.',
        descricao: [
            'A base reúne dados da <strong>Declaração de Incentivos, Benefícios e Renúncias Fiscais (DIRBI)</strong>, disponibilizados pela Receita Federal do Brasil.',
            'A DIRBI informa valores de benefícios e renúncias fiscais concedidos pela União, permitindo analisar a distribuição desses incentivos por <strong>beneficiário, município, Unidade Federativa, tributo e programa de isenção</strong>.',
            'A FiscalData organiza e padroniza as informações para facilitar a consulta, a comparação entre localidades e períodos e a construção de séries históricas a partir dos dados oficiais.',
        ],
        organizacao: 'Receita Federal do Brasil',
        // coberturaTemporal preenchida quando a série disponível em /dirbi-exercicios for confirmada
        periodicidade: 'Anual',
        fontesOriginais: [
            { label: 'Declaração de Incentivos, Benefícios e Renúncias Fiscais (DIRBI) — Receita Federal do Brasil' },
        ],
        atualizacao: 'A base é atualizada conforme a disponibilização de novos dados pela Receita Federal.',
    },
};

export const getDatasetMeta = (key) => DATASET_META[key] || null;
